import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

/**
 * EditableUserDetails Component
 *
 * A reusable component for displaying and editing user details inline.
 *
 * Props:
 * - userData: User data object (passed directly from parent)
 * - userId: Optional user ID for admin editing other users (if not provided, assumes self-edit)
 * - userType: Type of user (ankr_user, healthcare_user, patient, etc.)
 * - fieldConfig: Array of field configuration objects
 * - onSave: Callback function called after successful save
 * - onError: Callback function called on error
 */
const EditableUserDetails = ({ userData: initialUserData, userId, userType, fieldConfig, onSave, onError }) => {
  const [userData, setUserData] = useState(initialUserData);
  const [editedData, setEditedData] = useState(initialUserData || {});
  // If no userId provided (self-edit), start in edit mode directly
  const [isEditMode, setIsEditMode] = useState(!userId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Update local state when prop changes
  useEffect(() => {
    if (initialUserData) {
      setUserData(initialUserData);
      setEditedData(initialUserData);
    }
  }, [initialUserData]);

  const handleEdit = () => {
    setIsEditMode(true);
    setEditedData({ ...userData });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedData({ ...userData });
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Only send editable fields
      const editableFields = fieldConfig.filter(f => f.editable).map(f => f.name);
      const payload = {};
      editableFields.forEach(field => {
        if (editedData[field] !== undefined) {
          payload[field] = editedData[field];
        }
      });

      // Determine endpoint based on context
      let endpoint;
      if (!userId) {
        // Self-edit: use profile endpoint
        endpoint = '/admin/user/profile/';
      } else if (userType === 'sales' || userType === 'core_server_sales_user') {
        // Admin editing a sales user: use sales-specific endpoint with UUID
        endpoint = `/admin/sales/users/${userId}/`;
      } else {
        // Admin editing other user types: use general endpoint /api/admin/users/<user_type>/<user_id>/
        endpoint = `/admin/users/${userType}/${userId}/`;
      }

      const response = await adminApi.put(endpoint, payload);

      if (response.data?.success) {
        setUserData(editedData);
        setIsEditMode(false);
        if (onSave) onSave();
      } else {
        throw new Error(response.data?.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error saving user:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save changes';
      setError(errorMsg);
      if (onError) onError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setEditedData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const renderField = (field) => {
    const value = isEditMode ? editedData[field.name] : userData[field.name];
    const displayValue = value !== null && value !== undefined ? value : '-';

    // Read-only mode
    if (!isEditMode || !field.editable) {
      return (
        <div className="col-6 mb-3" key={field.name}>
          <label className="form-label small fw-bold text-muted mb-1">{field.label}:</label>
          <div className="text-dark">
            {field.type === 'checkbox' ? (
              <span className={`badge bg-${value ? 'success' : 'secondary'}`}>
                {value ? 'Yes' : 'No'}
              </span>
            ) : field.type === 'select' && field.options ? (
              field.options.find(opt => opt.value === value)?.label || displayValue
            ) : field.type === 'date' && value ? (
              new Date(value).toLocaleDateString()
            ) : (
              displayValue
            )}
          </div>
        </div>
      );
    }

    // Edit mode
    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <div className="col-6 mb-3" key={field.name}>
            <label className="form-label mb-1">{field.label}:</label>
            <input
              type={field.type}
              className="form-control"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              disabled={saving}
            />
          </div>
        );

      case 'select':
        return (
          <div className="col-6 mb-3" key={field.name}>
            <label className="form-label mb-1">{field.label}:</label>
            <select
              className="form-select"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              disabled={saving}
            >
              <option value="">Select {field.label}</option>
              {field.options && field.options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );

      case 'checkbox':
        return (
          <div className="col-6 mb-3" key={field.name}>
            <label className="form-label mb-1">{field.label}:</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={value || false}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                disabled={saving}
              />
              <label className="form-check-label">
                {value ? 'Active' : 'Inactive'}
              </label>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div className="col-12 mb-3" key={field.name}>
            <label className="form-label mb-1">{field.label}:</label>
            <textarea
              className="form-control"
              rows={3}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              disabled={saving}
            />
          </div>
        );

      default:
        return (
          <div className="col-6 mb-3" key={field.name}>
            <label className="form-label fw-bold text-muted mb-1">{field.label}:</label>
            <div className="text-dark">{displayValue}</div>
          </div>
        );
    }
  };

  if (error && !userData) {
    return (
      <div className="alert alert-danger mb-0">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="alert alert-warning mb-0">
        <i className="fas fa-info-circle me-2"></i>
        No user data available
      </div>
    );
  }

  return (
    <div className="editable-user-details">
      {/* Header with Edit/Save/Cancel buttons */}
      <div className="d-flex justify-content-end align-items-center mb-3">
        {!isEditMode && (
          <button className="btn btn-outline-primary" onClick={handleEdit} >
            Edit
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Fields grid */}
      <div className="row">
        {fieldConfig.map(field => renderField(field))}
      </div>        
      {isEditMode && (
        <>
              <button className="btn btn-primary me-2" onClick={handleSave} disabled={saving}
              >{saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    Save
                  </>
                )}
              </button>
              <button className="btn btn-secondary" onClick={handleCancel} disabled={saving} >
                Cancel
              </button></>
      )}
    </div>
  );
};

export default EditableUserDetails;
